import { ChangeEvent, useEffect, useRef, useState } from "react";

import { ImageSearch, DeleteForeverOutlined } from "@mui/icons-material";
import { CardMedia, IconButton } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ProdutoImageCropModal from "components/ProdutoImageCropModal";

import { getFileNameFromUrl } from "helpers/utils";
import {
  EVENTO_BANNER_HEIGHT,
  EVENTO_BANNER_WIDTH,
  EVENTO_CARD_HEIGHT,
  EVENTO_CARD_WIDTH,
  validateImageFilesExactDimensions,
} from "helpers/validateImageDimensions";

import { deleteDocumentoEvento } from "services/evento";
import { setMessage, useMaterialUIController } from "context";

interface Props {
  selectedFileBanner: (file: File | null) => void;
  selectedFileCard: (file: File | null) => void;
  imageUrlBanner?: string;
  imageUrlCard?: string;
  eventoId?: number;
}

type CropTarget = "banner" | "card";

function validateImageBasic(file: File, label: string) {
  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`${label}: use uma imagem JPG, PNG ou WEBP.`);
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error(`${label}: o arquivo deve ter no máximo 10 MB.`);
  }
}

export default function BannerEvento({
  selectedFileBanner,
  selectedFileCard,
  eventoId,
  imageUrlBanner,
  imageUrlCard,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const cardInputRef = useRef<HTMLInputElement>(null);
  const [previewBanner, setPreviewBanner] = useState<string>("");
  const [previewCard, setPreviewCard] = useState<string>("");
  const [localBanner, setLocalBanner] = useState(false);
  const [localCard, setLocalCard] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropTarget, setCropTarget] = useState<CropTarget | null>(null);

  useEffect(() => {
    setPreviewBanner(imageUrlBanner || "");
    setPreviewCard(imageUrlCard || "");
    setLocalBanner(false);
    setLocalCard(false);
  }, [imageUrlBanner, imageUrlCard]);

  useEffect(
    () => () => {
      if (previewBanner.startsWith("blob:")) {
        URL.revokeObjectURL(previewBanner);
      }
    },
    [previewBanner]
  );

  useEffect(
    () => () => {
      if (previewCard.startsWith("blob:")) {
        URL.revokeObjectURL(previewCard);
      }
    },
    [previewCard]
  );

  const resetCropState = () => {
    setCropModalOpen(false);
    setCropFile(null);
    setCropTarget(null);
  };

  const applyBannerFile = (file: File) => {
    if (previewBanner.startsWith("blob:")) URL.revokeObjectURL(previewBanner);
    setPreviewBanner(URL.createObjectURL(file));
    setLocalBanner(true);
    selectedFileBanner(file);
  };

  const applyCardFile = (file: File) => {
    if (previewCard.startsWith("blob:")) URL.revokeObjectURL(previewCard);
    setPreviewCard(URL.createObjectURL(file));
    setLocalCard(true);
    selectedFileCard(file);
  };

  const handleCropError = (message: string) => {
    setMessage(dispatch, {
      open: true,
      text: message,
      severity: "error",
    });
  };

  const handleCropConfirm = (cropped: File) => {
    if (cropTarget === "banner") {
      applyBannerFile(cropped);
    } else if (cropTarget === "card") {
      applyCardFile(cropped);
    }
    resetCropState();
  };

  async function handleBannerChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (bannerInputRef.current) bannerInputRef.current.value = "";
    if (!file) return;

    try {
      validateImageBasic(file, "Banner do evento");
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message,
        severity: "error",
      });
      return;
    }

    const invalid = await validateImageFilesExactDimensions(
      [file],
      EVENTO_BANNER_WIDTH,
      EVENTO_BANNER_HEIGHT
    );

    if (invalid.length === 0) {
      applyBannerFile(file);
      return;
    }

    setCropTarget("banner");
    setCropFile(file);
    setCropModalOpen(true);
    setMessage(dispatch, {
      open: true,
      text: `Ajuste o recorte do banner para ${EVENTO_BANNER_WIDTH} × ${EVENTO_BANNER_HEIGHT} px.`,
      severity: "info",
    });
  }

  async function handleCardChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (cardInputRef.current) cardInputRef.current.value = "";
    if (!file) return;

    try {
      validateImageBasic(file, "Card do evento");
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message,
        severity: "error",
      });
      return;
    }

    const invalid = await validateImageFilesExactDimensions(
      [file],
      EVENTO_CARD_WIDTH,
      EVENTO_CARD_HEIGHT
    );

    if (invalid.length === 0) {
      applyCardFile(file);
      return;
    }

    setCropTarget("card");
    setCropFile(file);
    setCropModalOpen(true);
    setMessage(dispatch, {
      open: true,
      text: `Ajuste o recorte do card para ${EVENTO_CARD_WIDTH} × ${EVENTO_CARD_HEIGHT} px.`,
      severity: "info",
    });
  }

  const removeBanner = async () => {
    try {
      if (!localBanner && eventoId && imageUrlBanner) {
        await deleteDocumentoEvento(
          Number(eventoId),
          "urlBanner",
          getFileNameFromUrl(imageUrlBanner)
        );
      }
      if (previewBanner.startsWith("blob:")) URL.revokeObjectURL(previewBanner);
      selectedFileBanner(null);
      setPreviewBanner("");
      setLocalBanner(false);
      if (bannerInputRef.current) bannerInputRef.current.value = "";
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message || "Não foi possível remover o banner.",
        severity: "error",
      });
    }
  };

  const removeCard = async () => {
    try {
      if (!localCard && eventoId && imageUrlCard) {
        await deleteDocumentoEvento(
          Number(eventoId),
          "urlImageDestaque",
          getFileNameFromUrl(imageUrlCard)
        );
      }
      if (previewCard.startsWith("blob:")) URL.revokeObjectURL(previewCard);
      selectedFileCard(null);
      setPreviewCard("");
      setLocalCard(false);
      if (cardInputRef.current) cardInputRef.current.value = "";
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: error.message || "Não foi possível remover o card.",
        severity: "error",
      });
    }
  };

  const cropWidth =
    cropTarget === "card" ? EVENTO_CARD_WIDTH : EVENTO_BANNER_WIDTH;
  const cropHeight =
    cropTarget === "card" ? EVENTO_CARD_HEIGHT : EVENTO_BANNER_HEIGHT;
  const cropTitle =
    cropTarget === "card"
      ? "Ajustar card do evento"
      : "Ajustar banner do evento";
  const cropPreviewLabel =
    cropTarget === "card" ? "Prévia do card" : "Prévia do banner";

  return (
    <>
      <Card id="banner" sx={{ mb: 2 }}>
        <MDBox p={2}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <MDBox height="100%" mt={0.5} lineHeight={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  Cadastro seu novo evento
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  Informe todos os detalhes do evento e publique-o para o público!
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox p={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <MDBox
                borderRadius="lg"
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={3}
                sx={{
                  position: "relative",
                  height: "240px",
                  border: ({ borders: { borderWidth, borderColor } }) =>
                    `${borderWidth[1]} dashed ${borderColor}`,
                }}
              >
                {previewBanner && (
                  <IconButton
                    onClick={removeBanner}
                    component="label"
                    sx={{
                      padding: "12px",
                      position: "absolute",
                      right: 2,
                      bottom: 2,
                      zIndex: 1,
                    }}
                  >
                    <DeleteForeverOutlined fontSize="large" color="error" />
                  </IconButton>
                )}
                {!previewBanner && (
                  <IconButton component="label" sx={{ padding: "80px" }}>
                    <input
                      ref={bannerInputRef}
                      name="fileAvatar"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      type="file"
                      onChange={handleBannerChange}
                    />
                    <ImageSearch fontSize="large" />
                  </IconButton>
                )}
                {previewBanner && (
                  <CardMedia
                    src={previewBanner}
                    component="img"
                    title="image"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "85%",
                      margin: 0,
                      boxShadow: ({ boxShadows: { md } }) => md,
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                )}
              </MDBox>
              <MDTypography variant="caption" color="text">
                Banner do evento — proporção {EVENTO_BANNER_WIDTH} ×{" "}
                {EVENTO_BANNER_HEIGHT} px (ajuste o recorte ao enviar)
              </MDTypography>
            </Grid>

            <Grid item xs={12} md={4}>
              <MDBox
                borderRadius="lg"
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={3}
                sx={{
                  position: "relative",
                  height: "240px",
                  border: ({ borders: { borderWidth, borderColor } }) =>
                    `${borderWidth[1]} dashed ${borderColor}`,
                }}
              >
                {previewCard && (
                  <IconButton
                    onClick={removeCard}
                    component="label"
                    sx={{
                      padding: "12px",
                      position: "absolute",
                      right: 2,
                      bottom: 2,
                      zIndex: 1,
                    }}
                  >
                    <DeleteForeverOutlined fontSize="large" color="error" />
                  </IconButton>
                )}
                {!previewCard && (
                  <IconButton component="label" sx={{ padding: "80px" }}>
                    <input
                      ref={cardInputRef}
                      name="fileCard"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      type="file"
                      onChange={handleCardChange}
                    />
                    <ImageSearch fontSize="large" />
                  </IconButton>
                )}
                {previewCard && (
                  <CardMedia
                    src={previewCard}
                    component="img"
                    title="image"
                    sx={{
                      maxWidth: "100%",
                      margin: 0,
                      boxShadow: ({ boxShadows: { md } }) => md,
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                )}
              </MDBox>

              <MDTypography variant="caption" color="text">
                Card do evento — proporção {EVENTO_CARD_WIDTH} ×{" "}
                {EVENTO_CARD_HEIGHT} px (ajuste o recorte ao enviar)
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <ProdutoImageCropModal
        open={cropModalOpen}
        file={cropFile}
        maxWidth={cropWidth}
        maxHeight={cropHeight}
        title={cropTitle}
        previewLabel={cropPreviewLabel}
        onClose={resetCropState}
        onConfirm={handleCropConfirm}
        onError={handleCropError}
      />
    </>
  );
}
